/**
 * API の呼び出し。**画面は必ずここを通す。**
 *
 * ## ★トークンを触らない
 *
 * セッションは `HttpOnly` Cookie で運ばれる。**JavaScript からは読めないし、書けない。**
 * `credentials: "same-origin"` を付けるだけで、ブラウザが勝手に付けてくれる。
 * 画面側にトークンを持たせない＝XSS が起きても持ち出せない。
 *
 * ## ★権限判定をここに書かない
 *
 * 「staff だからこのボタンを隠す」は**見た目の都合**であって、
 * **安全の担保はサーバ側にしかない**（絶対ルール1）。
 * 隠し忘れても漏れないよう、サーバが落とす。ここでの分岐は使いやすさのため。
 */
import type { ReportFigures } from "../domain/dailyReport.js";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    /**
     * ★応答の本文をそのまま持たせる。
     *
     * 締め申請が止まったときの `blocking`（止めている検査の一覧）のように、
     * **エラーにこそ中身がある**場合がある。文言だけにすると
     * 「できません」しか出せなくなり、利用者は次に何をすればよいか分からない。
     */
    public readonly body: unknown = undefined,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface ErrorBody {
  error?: string;
  message?: string;
}

/**
 * ★パスワードの決まり（画面の案内と、断られたときの文言に同じ文を使う）。
 * ★**`src/auth/password.ts` の `PASSWORD_RULE_TEXT` と同じ文にする。**
 *   画面のバンドルは `src/auth` を読み込まない（読み込ませない）ので写しになる。
 *   食い違いは `tests/ui/password-change-view.test.ts` が突き合わせて止める。
 */
export const PASSWORD_RULE_TEXT = "8文字以上で、大文字・小文字・数字・記号のうち2種類以上を含めてください";

/** ★`src/auth/password.ts` の `LOGIN_ID_RULE_TEXT` の写し（SELF-LOGIN-ID）。突き合わせは同じテスト */
export const LOGIN_ID_RULE_TEXT = "ログインIDは半角英数字と . _ - の3〜32文字にしてください";

/** 利用者に見せる文言。★サーバの内部事情をそのまま出さない */
const MESSAGES: Record<string, string> = {
  submit_blocked: "自動検算が通っていないため締め申請できません",
  authentication_failed: "ログインIDまたはパスワードが違います",
  invite_unusable: "この招待は使えません。すでに登録した方は、ログイン画面から入れます。招待した人に、新しい招待を出してもらってください",
  too_many_attempts: "試行が続いています。しばらく待ってからやり直してください",
  unauthorized: "ログインの有効期限が切れました。もう一度ログインしてください",
  forbidden: "この操作を行う権限がありません",
  password_change_required: "パスワードの変更が必要です",
  period_closed: "この月は締め処理が済んでいるため変更できません",
  weak_password: PASSWORD_RULE_TEXT,
  current_password_mismatch: "現在のパスワードが違います",
  same_password: "今のパスワード・配られた仮パスワードとは違うものを設定してください",
  internal_error: "エラーが発生しました。しばらくしてからお試しください",
};

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: "same-origin",
    headers: { "content-type": "application/json", ...(init.headers ?? {}) },
  });

  if (!res.ok) {
    let body: ErrorBody = {};
    try {
      body = (await res.json()) as ErrorBody;
    } catch {
      /* 本文が JSON でないこともある */
    }
    const code = body.error ?? "internal_error";
    throw new ApiError(
      res.status,
      code,
      body.message ?? MESSAGES[code] ?? "エラーが発生しました",
      body,
    );
  }
  return (await res.json()) as T;
}

export interface Me {
  /** ★氏名ではなくログインID。理由は `src/api/app.ts` の /me を参照 */
  loginId: string;
  mustChangePassword: boolean;
  /** ★初回のパスワード変更で、本人がログインIDも決める（SELF-LOGIN-ID） */
  mustChooseLoginId: boolean;
  /** ★true なら打刻・シフトは kintai（`docs/19` D-1）。タブを出さない。★無い＝旧サーバ（出す） */
  attendanceInKintai?: boolean;
  storeId: string;
  role: "staff" | "leader" | "manager" | "owner";
  canViewPayroll: boolean;
}

export type DocumentType = "DELIVERY_NOTE" | "RECEIPT" | "POS_CLOSE";
export type DocumentWorkflowStatus = "CAPTURED" | "READING" | "NEEDS_REVIEW" | "REGISTERED" | "FAILED";
export interface IntakeDocument {
  id: string;
  doc_type: DocumentType;
  business_date: string;
  original_filename: string | null;
  mime_type: string;
  file_size: string;
  image_width: number;
  image_height: number;
  workflow_status: DocumentWorkflowStatus;
  ocr_status: "PENDING" | "DONE" | "FAILED" | "SKIPPED";
  ocr_draft?: DocumentOcrDraft | null;
  ocr_draft_version?: number;
  ocr_error_code?: string | null;
  uploaded_at: string;
  /** 登録先のテーブル（`purchases` / `petty_cash_entries` / `daily_sales`）。未登録は null */
  registered_target?: string | null;
  /** 登録先のレコード。★訂正はこの行へ移動して、登録先の正規経路で行う（文書を未登録へ戻さない） */
  registered_target_id?: string | null;
}
export interface DocumentOcrDraft { documentDate:string|null; settledAt:string|null; vendorText:string|null; totalAmount:number|null; cashSales:number|null; cardSales:number|null; otherSales:number|null; cashBalance:number|null; guestCount:number|null; groupCount:number|null; /** 領収書の支払方法の読取値。★旧い下書きにはキーが無い（undefined は null と同じ扱い） */ paymentMethod?:"CASH"|"CREDIT"|"OTHER"|null }

/**
 * ★入力の**業務上限**（`migrations/0061`）。画面はこれを `maxlength` の桁数に使う。
 *
 * ★**画面に数字を写して持たない。** 写すと、パラメータを変えた日に
 *   「打てるのに 400 になる欄」か「打てないのに通る欄」が静かに生まれる。
 * ★止める権威はサーバ（`upsertDailySales` / `reviseStaffWage`）。これは打鍵を減らすだけ。
 */
export interface InputCeilings {
  /** 客数（人） */
  guests: number;
  /** ★組数（組）。客数とは別のキー（0064）。共用だと片方が必ず緩くなる */
  groups: number;
  /** 売上（税込）・現金・カード・その他（円） */
  amount: number;
}

/**
 * ★0065（INPUT-ceiling-phase2）で足した経路の上限。
 *
 * ★★**ロス金額はここに無い**（依頼者判断 2026-09-04）。
 *   ロスは `staff` も書くが `GET /api/loss/:date` は `leader` 以上で、
 *   画面へ**先回りして**値を渡す経路が無い（＝`maxlength` が付かない）。
 *   ★**「値が届かない」ではない**——超過したときの 400 は数字を出す（2026-09-05 に訂正）。
 *   詳細は `src/auth/permissions.ts` の `INPUT_CEILING_AUDIENCE`。
 * ★目標の2本は**書ける人にだけ**返る（read は全ロールのため）。読むだけの人には `undefined`。
 */
export interface PurchaseCeilings {
  /** 仕入金額（1件・円） */
  amount: number;
}

export interface PettyCashCeilings {
  /** ★小口現金の**出金**（1件・円）。入金とは桁が2つ違うので別のキー */
  amount: number;
  /** ★小口現金の**入金＝補充**（1件・円） */
  inAmount: number;
  /** ★実査額（金庫の残高・円）。1件の出納とは別のキー。桁が1つ違う */
  countedAmount: number;
}

export interface InventoryCeilings {
  /** 期末棚卸金額（円） */
  closingAmount: number;
}

/**
 * ★月次目標の上限は**`target_key` ごと**（`monthly_sales_excl_tax` は円、`monthly_guests` は人）。
 *
 * ★★同じ列（`monthly_targets.target_value`）に**単位の違う2つの目標**が入っており、
 *   0064 までは列の実容量を両方へ使い回していた。売上に合う上限を客数へ当てると
 *   3,000万人 が通る——0064 が客数と組数で潰したのと同じ形。
 * ★キーを列挙せず `Record` にしてあるのは、サーバが `MONTHLY_TARGET_DEFS` から導いているため
 *   （`docs/05` §3.4 の段階2＝目標FL が増えたとき、画面側を直さなくても上限が届く）。
 */
export type MonthlyTargetCeilings = Readonly<Record<string, number>>;

export interface SalesTargetCeilings {
  /** ★日別売上目標（円）。日次売上と同じキー（`input_ceiling_daily_sales`） */
  targetSales: number;
}

export interface DailySales {
  businessDate: string;
  salesInclTaxDinner: string;
  guestsDinner: number;
  groupsDinner: number;
  cashAmount: string;
  cardAmount: string;
  otherAmount: string;
  note: string | null;
}

export type ClosingStatus = "OPEN" | "SUBMITTED" | "CLOSED" | "REJECTED" | "REOPENED";

export interface LossRecord {
  id: string;
  businessDate: string;
  lossType: "WASTE" | "STAFF_MEAL" | "SERVICE";
  amount: string;
  note: string | null;
}

export interface Vendor {
  id: string;
  vendorCode: string;
  name: string;
  category: "FOOD" | "DRINK" | "OTHER";
}

export interface Purchase {
  id: string;
  businessDate: string;
  vendorId: string;
  vendorName: string;
  category: "FOOD" | "DRINK" | "OTHER";
  amount: string;
  note: string | null;
}

export interface PettyCashAccount {
  id: string;
  accountCode: string;
  name: string;
  accountType: "PURCHASE" | "SUPPLIES" | "MISC" | "TRANSPORT" | "OTHER";
}

export interface PettyCashEntry {
  id: string;
  accountId: string;
  accountName: string;
  paymentMethod: "CASH" | "CREDIT" | "OTHER";
  direction: "IN" | "OUT";
  amount: string;
  description: string | null;
}

export interface CashBalance {
  /** ★現金だけで数えた残高。クレジットは含めない（D-13） */
  cashBalance: number;
  creditTotal: number;
  /** false のとき cashBalance は「記録開始以降の増減」であって手元現金ではない */
  hasOpening: boolean;
}

export interface CashCount {
  businessDate: string;
  countedAmount: string;
  bookAmount: string;
  difference: string;
  reason: string | null;
}

/** 適用日で解決したパラメータ1件（`src/infra/parameters.ts` の `ResolvedFromValue`） */
export interface ResolvedParameter {
  key: string;
  /** ★画面に出す名前は定義から来る。UI に対応表を持たない */
  label: string;
  unit: string | null;
  /** 何の値か／どこに効くか（定義の説明文）。★画面側に対応表を持たない */
  description: string | null;
  value: string;
  valueType: string;
  validFrom: string;
  validTo: string | null;
}

/** ★既定値で解決したもの。**「当時の値」ではない**ので別の配列で来る */
export interface DefaultedParameter {
  key: string;
  label: string;
  unit: string | null;
  description: string | null;
  value: string;
  valueType: string;
}

export interface ParametersResult {
  resolved: ResolvedParameter[];
  usingDefault: DefaultedParameter[];
  unresolved: {
    key: string;
    label: string;
    unit: string | null;
    description: string | null;
    valueType: string;
  }[];
}

/**
 * 最低賃金の判定（C-14）。
 * ★`skipped` は**問題なしではない**。判定できなかったという意味。
 */
export type MinimumWageCheck =
  | { status: "passed"; minimumWage: number; checked: number; violations: [] }
  | {
      status: "failed";
      minimumWage: number;
      checked: number;
      violations: { staffId: number; baseHourlyWage: number; shortfall: number }[];
    }
  | { status: "skipped"; minimumWage: number | null; checked: 0; reason: string };

/**
 * 日付を動かさずに出す最低賃金の警告（M5b / 案B）。
 * ★`skipped` は**問題なしではない**。`passed` は判定した件数を必ず持つ。
 */
export type MinimumWageAlert =
  | {
      status: "failed";
      firstFailure: { date: string; count: number; minimumWage: number };
      skipped: { date: string; reason: string }[];
      checkedDates: string[];
    }
  | { status: "skipped"; skipped: { date: string; reason: string }[]; checkedDates: string[] }
  | { status: "passed"; checked: number; checkedDates: string[] };

/**
 * 未退勤・不整合の1件（docs/02 §4.3「検出して出す」/ 権限マトリクス §3.6・R0102 A-3）。
 * ★氏名は含まない。宛先は本人 R(個)・manager 以上 R
 */
export interface AttendanceAnomaly {
  staffId: string;
  businessDate: string;
  reason: string;
}

/**
 * 打刻（出勤・休憩開始・休憩終了・退勤）1件（権限マトリクス §3.6・R0102 A-3 / A-4）。
 * ★氏名・時給は含まない。`(個)` の絞り込みはサーバが行う（AC-3）
 */
export type PunchType = "CLOCK_IN" | "BREAK_START" | "BREAK_END" | "CLOCK_OUT";

export interface PunchEvent {
  id: string;
  staffId: string;
  punchType: PunchType;
  occurredAt: string;
  businessDate: string;
}

/**
 * 打刻の訂正1件（権限マトリクス §3.6・R0102 A-5）。
 * ★「誰が」は `correctedAs`（本人 / manager 以上）の区別まで。`correctedBy` は
 *   users の id で、ログインIDへの解決はしていない（監査ログ F-22 で追える）
 */
export interface PunchCorrection {
  id: string;
  punchId: string;
  staffId: string;
  field: "occurred_at" | "punch_type";
  oldValue: string;
  newValue: string;
  correctedAs: "SELF" | "OTHER";
  correctedBy: string;
  correctedAt: string;
  reason: string;
}

/** `GET /api/punches` の応答（events と corrections は常に対で返る） */
/**
 * 確定シフト（予定）1件（§3.7 / R0102 B-2・B-5）。
 * ★**氏名を持つ**（`docs/05` §3.7 の例外・依頼者判断 Q-S1）。
 *   ★時給・個人別人件費は1マスも載らない。
 */
export interface Shift {
  id: string;
  staffId: string;
  /** ★氏名（§3.7 の例外）。**キー名を変えない**——別名は砦を素通りする */
  displayName: string;
  businessDate: string;
  /** ★所属営業日の0時起点の分。24時超え表現（`docs/02` §4.4） */
  startMinutes: number;
  endMinutes: number;
  /** ★［開始, 終了）の対の集合。合計分に畳まない（中抜けが消える） */
  breaks: { startMinutes: number; endMinutes: number }[];
  updatedAt: string;
  /**
   * ★公開した時刻。`null` は**下書き**（R0102 B-6 / 依頼者判断 Q-S12）。
   * ★`staff` / `leader` には下書きの行がそもそも返らないので、彼らの手元では常に非 `null`。
   */
  publishedAt: string | null;
}

export interface ShiftsResult {
  /** ★店舗全体・氏名つき。**ロールで中身が変わらない**（§3.7 は全ロール `R`） */
  shifts: Shift[];
  /**
   * ★紐付けられている自分の従業員ID。**表示の絞り込み（「自分の分だけ」）にしか使わない。**
   * 空なら、この端末のアカウントはまだ従業員と紐付けられていない。
   */
  myStaffIds: string[];
}

/**
 * シフトの変更・公開への気づき（§3.7 の Q-S6 / R0102 B-7）。
 *
 * ★**`linked` と「変更なし」を取り違えない。** 未紐付け（`linked: false`）は
 *   「変わっていない」ではなく「そもそも自分の予定という概念が無い」。
 *   `owner` は構造上ほぼ恒久的に未紐付けで、この状態を日常的に踏む。
 */
export interface ShiftNoticeResult {
  linked: boolean;
  /**
   * 変わっていなければ `null`。`month` は変わった行のうち最も早い営業日の月（`YYYY-MM`）。
   * ★`canceled` は、変わった行に**取り消されたもの**が含まれるか——
   *   一覧は `ACTIVE` しか出さないので、これを帯に書かないと消えた事実が画面から消える。
   */
  notice: { month: string; canceled: boolean } | null;
}

/**
 * 希望シフト・修正申請1件（§3.7 / R0102 B-3・★B-4）。
 * ★**氏名は `can_view_payroll` のロールにだけ載る**（`redactPayroll` が落とす）。
 *   §3.7 の氏名の例外（確定シフトの閲覧）を**この経路では使っていない。**
 */
export interface ShiftRequest {
  id: string;
  staffId: string;
  /** ★`staff` / `leader` の手元では `undefined`（サーバが落としている） */
  displayName?: string;
  /** ★希望＝`WISH`／修正＝`CHANGE`。**2本目の状態機械を作らず、同じテーブルの種別で分ける** */
  kind: "WISH" | "CHANGE";
  /** ★`CHANGE` のときだけ非 `null`。対象の確定シフトの id（`Shift["id"]`） */
  shiftPlanId: string | null;
  businessDate: string;
  startMinutes: number;
  endMinutes: number;
  status: "SUBMITTED" | "APPROVED" | "REJECTED" | "WITHDRAWN";
  /** ★修正申請の理由（`CHANGE` のみ・自由文）。`textContent` で描くこと */
  reason: string | null;
  /** ★却下理由。**本人の目に触れる唯一の経路**（監査ログは manager 以上）。自由文 */
  rejectReason: string | null;
  decidedAt: string | null;
}

export interface ShiftRequestsResult {
  requests: ShiftRequest[];
  /**
   * ★**いま出せる最短の営業日と、その締切**（R0102 B-3 周回4）。
   * ★**画面で計算しない。** 粒度も締切日数も営業日の切り替わりもサーバが1箇所で決める。
   * `null` は「いま出せない」（募集のパラメータが未設定・値域外など）。
   */
  window: { from: string; deadline: string } | null;
  /** ★このアカウントが従業員と紐付けられているか。`false` なら希望を出しようがない（§2.1） */
  linked: boolean;
  /** ★未紐付けのときだけ届く説明。**黙って空を返さない**（§2.1 の明文） */
  message?: string;
}

/**
 * 確定シフトの修正申請の一覧（§3.7 / R0102 B-4）。
 * ★**`window` は無い。** 修正申請は確定後にしか出せず、募集の締切という概念を持たない
 * （希望シフトの `ShiftRequestsResult` と違う形になっているのはそのため）。
 */
export interface ShiftChangeRequestsResult {
  requests: ShiftRequest[];
  /** ★このアカウントが従業員と紐付けられているか。`false` なら修正申請を出しようがない（§2.1） */
  linked: boolean;
  /** ★未紐付けのときだけ届く説明。**黙って空を返さない**（§2.1 の明文） */
  message?: string;
}

export interface ShiftInput {
  staffId: string;
  businessDate: string;
  startMinutes: number;
  endMinutes: number;
  breaks: { startMinutes: number; endMinutes: number }[];
}

export interface PunchLogResult {
  events: PunchEvent[];
  corrections: PunchCorrection[];
  unlinked?: true;
  message?: string;
}

/**
 * 自動検算の1件（計算定義書 §9・M9a）。
 *
 * ★`status` は3値。**`skipped` を「問題なし」と読ませない**——
 *   検証できなかっただけで、締め申請は通らない（§9.1）。
 */
export interface IntegrityCheck {
  code: string;
  name: string;
  status: "passed" | "failed" | "skipped";
  expected: number | null;
  actual: number | null;
  difference: number | null;
  detail: Record<string, unknown> | null;
  /** ★`skipped` のときだけ入る。**利用者が読む文**（開発の記号を書かない） */
  skippedReason: string | null;
}

export interface ClosingChecks {
  month: string;
  closingStatus: ClosingStatus;
  /** M10：差戻し・締め解除の理由。記録が無ければ `null` */
  rejectedReason: string | null;
  reopenedReason: string | null;
  checks: IntegrityCheck[];
  /** ★`passed` 以外。画面が探し直さなくてよいようにサーバが分けて返す */
  blocking: IntegrityCheck[];
}

/** 締め申請が止まったときの本文（409） */
export interface SubmitBlocked {
  error: "submit_blocked";
  message: string;
  closingStatus: ClosingStatus;
  blocking: IntegrityCheck[];
}

/** 監査ログ1件の、変わった列（F-22a） */
export interface AuditChange {
  field: string;
  before: string | null;
  after: string | null;
}

export interface AuditEntry {
  id: string;
  /** 日本時間の `YYYY-MM-DD HH:MM` */
  occurredAt: string;
  action: "INSERT" | "UPDATE" | "DELETE";
  tableName: string;
  recordPk: string;
  /** 操作者のログインID。★不明なら `null`（システム経由・移行など） */
  actor: string | null;
  /** ★この行が店舗に紐づいているか。`users` / `staff_wages` などは紐づかない */
  storeScoped: boolean;
  /**
   * ★接続元アドレス。**本番でしか入らない**（A6-1）ので、開発では常に `null`。
   *   A6 より前に積まれた行も `null`。★**画面は「記録なし」と書く。黙って消さない**
   */
  ip: string | null;
  /** ★申告された端末情報。**利用者が選んだ文字列。** `textContent` で入れること */
  userAgent: string | null;
  /** ★変わった列だけ。行全体は返ってこない */
  changes: AuditChange[];
}

export interface AuditPage {
  entries: AuditEntry[];
  total: number;
  offset: number;
  pageSize: number;
  /** ★既定で畳んだログインの記録の件数。**隠していないことを画面が言うため** */
  hiddenSessions: number;
  tables: { tableName: string; count: number }[];
  /** ★絞り込みに出す操作の種類。実際に記録がある操作だけ */
  actions: { action: string; count: number }[];
  /** ★絞り込みに出す操作者。実際に記録がある人だけ */
  actors: { actor: string; count: number }[];
  /**
   * ★絞り込みに出す接続元。**記録があるアドレスだけ**（A6-2b）。
   *   ★開発では常に空（A6-1）。**空なら画面は選択肢を出さない。**
   */
  sources: { ip: string; count: number }[];
}

/** システム状態の自己診断（F-19）。★形は `src/infra/selfDiagnosis.ts` と1:1。値は含まない（絶対ルール1） */
export interface ExpiringParameter {
  paramKey: string;
  name: string;
  validTo: string;
  /** 0以下なら既に期限切れ */
  daysRemaining: number;
}
export interface SelfDiagnosisResult {
  expiringParameters: ExpiringParameter[];
  pendingDocuments: { count: number; oldestBusinessDate: string | null };
  unclosedMonths: { yearMonth: string; status: string }[];
  /** kintai 受け口の健全性（KINTAI-IMPORT AC-13）。値は含まない（絶対ルール1） */
  kintaiIngest: {
    enabled: boolean;
    lastReceivedAt: string | null;
    heldSessionCount: number;
    /** 理由コード×年月ごとの内訳（B3）。氏名・時給・分数は含まない（営業日の日付は含む） */
    heldBreakdown: { reasonCode: string; yearMonth: string | null; count: number; workDates: string[] }[];
    warning: boolean;
    /** 直近30日に kintai から氏名が変わった従業員の件数（NAME-FROM-KINTAI）。氏名は含まない */
    nameChangesLast30Days: number;
  };
}

/** 日次決算・月次P/L（#12）。★形は `src/infra/profitLoss.ts` と1:1 */
export interface ComputedAmount {
  amount: number | null;
  uncomputableReason: string | null;
}
export interface ProfitLossAmounts {
  salesExclTax: ComputedAmount;
  purchases: ComputedAmount;
  grossProfit: ComputedAmount;
  operatingProfit: ComputedAmount;
}
export interface ProfitLoss {
  date: string;
  yearMonth: string;
  day: ProfitLossAmounts;
  cumulative: ProfitLossAmounts;
  missingDates: string[];
  calendarUnavailableReason: string | null;
  /** §6.1 日次固定費（配賦＋人件費＋小口経費）。出せなければ `null`（理由は `fixedCostReasons`） */
  dayFixedCosts: number | null;
  cumulativeFixedCosts: number | null;
  /** 速報原価率（棚卸前・`docs/02` §2.4）。出せなければ `value = null`＋理由 */
  provisionalCostRate: { value: number | null; reason: string | null };
  month: {
    salesExclTax: ComputedAmount;
    fixedCosts: number | null;
    /** ★店舗合計の人件費。宛先は manager 以上（`docs/05` §3.2） */
    laborCost: number | null;
    laborCostRate: number | null;
    flRate: number | null;
    cost: ComputedAmount;
    costRate: number | null;
    inventoryAdjustment: ComputedAmount;
    grossProfit: ComputedAmount;
    grossProfitRate: number | null;
    operatingProfit: ComputedAmount;
    operatingProfitRate: number | null;
  };
  /** 推移（1日〜`date`・`docs/02` §7.1）。未入力・計算できない日は `null` */
  trend: { date: string; salesExclTax: number | null; operatingProfit: number | null }[];
  /** 目標対比（`docs/02` §7.1）。未設定・出せないものは `null` */
  targets: {
    monthTargetProfit: number | null;
    targetCostRate: number | null;
    targetLaborCostRate: number | null;
    targetFlRate: number | null;
    dailyProfitTarget: number | null;
    profitTargetCumulative: number | null;
    profitAchievementCumulative: number | null;
    profitVarianceCumulative: number | null;
  };
  fixedCostReasons: string[];
  pettyExpenseRefunds: number;
  excludedOtherPurchases: number;
  hasImportedPurchases: boolean;
}

/** 帳票「日次決算書」（F-13・`docs/02` §7.2）。★形は `src/infra/dailyStatement.ts` と1:1。空欄は `null` */
export interface DailyStatementRow {
  date: string;
  /** 0 = 日曜 */
  weekday: number;
  isOpen: boolean | null;
  salesTarget: number | null;
  guestsTarget: number | null;
  /** 目標利益（日）。定休日・利益目標が未設定は `null` */
  profitTarget: number | null;
  sales: number | null;
  salesCumulative: number | null;
  salesTargetCumulative: number | null;
  salesVarianceCumulative: number | null;
  salesAchievementCumulative: number | null;
  guests: number | null;
  guestsVariance: number | null;
  perGuest: number | null;
  purchases: number | null;
  /** 率は 0〜1 の小数（画面で % にする）。★原価率は仕入ベースの概算 */
  costRate: number | null;
  costRateCumulative: number | null;
  laborCostRate: number | null;
  laborCostRateCumulative: number | null;
  flRateCumulative: number | null;
  grossProfit: number | null;
  grossProfitCumulative: number | null;
  fixedCosts: number | null;
  fixedCostsCumulative: number | null;
  operatingProfit: number | null;
  operatingProfitCumulative: number | null;
  /** 利益達成（0〜1 の小数）。目標利益の累計が 0 以下・出せない日は `null` */
  profitAchievementCumulative: number | null;
}
export interface DailyStatement {
  yearMonth: string;
  shopName: string;
  /** Asia/Tokyo・`YYYY-MM-DDTHH:MM:SS` */
  generatedAt: string;
  closingStatus: ClosingStatus;
  businessDays: number | null;
  monthTargetSales: number | null;
  monthTargetGuests: number | null;
  /** 利益目標（円・負もあり得る）。率は 0〜1 の小数。未設定は `null`（manager 以上の目標。この帳票は owner のみ） */
  monthTargetProfit: number | null;
  targetCostRate: number | null;
  targetLaborCostRate: number | null;
  targetFlRate: number | null;
  targetPerGuest: number | null;
  targetProfitRatio: number | null;
  rows: DailyStatementRow[];
  month: {
    salesExclTax: number | null;
    cost: number | null;
    costRate: number | null;
    grossProfit: number | null;
    grossProfitRate: number | null;
    /** ★店舗合計。owner だけが受け取る（`docs/05` §3.2） */
    laborCost: number | null;
    laborCostRate: number | null;
    flRate: number | null;
    fixedCosts: number | null;
    operatingProfit: number | null;
    operatingProfitRate: number | null;
    fixedCostsAverage: number | null;
    operatingProfitAverage: number | null;
    /** 平均を割った日数（売上が入力された営業日） */
    averageDays: number;
  };
  notes: {
    missingDates: string[];
    calendarUnavailableReason: string | null;
    fixedCostReasons: string[];
    hasImportedPurchases: boolean;
    excludedOtherPurchases: number;
  };
}

/**
 * 異常検知（F-16・`docs/02` §9.2）。★形は `src/infra/anomalyDetection.ts` と1:1。
 * 原価率・人件費率は日次の値が定義されていないため常に `indeterminate`
 * （`src/domain/costProfit.ts` の注記。§3.2 の別行として新設しない）。
 */
export type AnomalyMetricKey = "sales_excl_tax" | "guests" | "cost_rate" | "labor_cost_rate";

export type AnomalyJudgement =
  | { status: "indeterminate"; reason: string; sampleCount: number }
  | {
      status: "normal" | "anomaly";
      value: number;
      median: number;
      mad: number;
      deviation: number;
      thresholdK: number;
      sampleCount: number;
    };

export interface DailyAnomalies {
  date: string;
  lookbackWeeks: number;
  thresholdK: number;
  metrics: Record<AnomalyMetricKey, AnomalyJudgement>;
}

/**
 * 当日・当月の売上要約（`docs/02` §2.3 ／ `docs/05` §3.8 ／ R03-C-3）。
 *
 * ★`null` は 0 ではない。当日の行が無い（`recorded: false`）／目標未設定／目標0／
 *   カレンダー未整備（`calendarUnavailableReason`）を、画面は言い分ける（`sales-summary-view.ts`）。
 * ★形は `src/infra/salesSummary.ts` と1:1。項目を足すときは先に `docs/02` §2.3 の表へ。
 */
export interface SalesSummary {
  date: string;
  yearMonth: string;
  day: {
    recorded: boolean;
    inclTax: number;
    exclTax: number;
    guests: number;
    perGuest: number | null;
    targetSales: number | null;
    salesAchievement: number | null;
    targetGuests: number | null;
    guestsAchievement: number | null;
  };
  month: {
    recordedDays: number;
    inclTax: number;
    exclTax: number;
    guests: number;
    perGuest: number | null;
    dailyAverage: number | null;
    calendarUnavailableReason: string | null;
    targetSales: number | null;
    salesAchievement: number | null;
    targetGuests: number | null;
    guestsAchievement: number | null;
  };
}

/**
 * 月次目標（`docs/02` §8.1 の2キー）。★**キーは常に全件届く。**
 * 未設定は `value: null`——0（目標を0と決めた月）と区別する。
 */
export interface MonthlyTarget {
  key: string;
  label: string;
  unit: string;
  value: string | null;
}

/**
 * 日別売上目標。★その月の**暦日が全日ぶん**届く。未設定は `null`。
 *
 * ★**客数は無い。** `docs/02` §8 の日別客数目標は
 * 「月間客数目標 ÷ 営業日数」の派生値で、日付別に入れるものではない。
 */
export interface DailySalesTarget {
  businessDate: string;
  targetSales: string | null;
}

/**
 * 営業日カレンダーの1日（§3.3 / `docs/02` §1.1）。
 *
 * ★`registered: false` は**まだ登録されていない日の下書き**（定休日パラメータから作った案）。
 *   下書きのままでは営業日数は数えられない——保存して初めて数えられる。
 */
export interface CalendarDay {
  businessDate: string;
  isOpen: boolean;
  isHoliday: boolean;
  closeReason: string | null;
  registered: boolean;
}

export interface CalendarDraft {
  month: string;
  /** 暦日を全日ぶん。日付順 */
  days: CalendarDay[];
  /** ★全日ぶん登録済みか。`false` のあいだ営業日数・日商平均・固定費の配賦が出せない */
  registered: boolean;
}

/** 固定費の科目（`docs/02` §6.1 ／ M5-FIXED-COST） */
export interface ExpenseAccount {
  id: string;
  accountCode: string;
  name: string;
  /** `BUSINESS_DAY` 営業日割 / `CALENDAR_DAY` 暦日割 / `NONE` 配賦なし */
  allocMethod: "BUSINESS_DAY" | "CALENDAR_DAY" | "NONE";
  /** ★`DERIVED` は人件費・小口現金からの連携専用。**手入力させない** */
  source: "MANUAL" | "DERIVED";
  isActive: boolean;
}

/** 科目 + その月の月額。★`amount` の `null` は未入力（0 と区別する） */
export interface FixedCost extends ExpenseAccount {
  amount: string | null;
}

export interface FixedCostCeilings {
  /** 月額（円）。★`fixed_cost_monthly.amount` = `numeric(12,2)` の整数部 */
  amount: number;
}

export interface Inventory {
  category: "FOOD" | "DRINK";
  /**
   * ★前月の期末をそのまま繰り越した値。入力させない（計算定義書 §3.2）。
   * **前月が未入力なら `null`**（0 と区別する）
   */
  openingAmount: string | null;
  /** ★`null` は未入力。0（棚卸したが在庫なし）と区別する */
  closingAmount: string | null;
  countedAt: string | null;
}

/**
 * 従業員1名（`src/infra/staff.ts` の `StaffRow`）。
 *
 * ★`displayName` は給与情報。**`canViewPayroll` でない相手には
 *   そもそもサーバが返さない**（フロントで隠しているのではない）。
 *   `undefined` になり得るのはそのため。
 */
export interface Staff {
  id: string;
  staffCode: string;
  displayName?: string;
  employmentType: "PART_TIME" | "FULL_TIME";
  hiredOn: string | null;
  retiredOn: string | null;
  isActive: boolean;
}

/**
 * ログインアカウント1件（F-21 / `src/infra/accounts.ts` の `AccountRow`）。
 *
 * ★`displayName` は**アカウントの表示名**で、従業員の氏名（給与情報）ではない。
 *   この画面へ来られるのは manager 以上なので、サーバは伏せずに返す。
 */
export interface Account {
  id: string;
  loginId: string;
  displayName: string;
  role: "staff" | "leader" | "manager" | "owner" | null;
  isActive: boolean;
  retiredOn: string | null;
  lastLoginAt: string | null;
  staffRetiredOn: string | null;
  /** ★辞めた人のアカウントが、まだ入れる状態で残っている */
  staffRetiredButActive: boolean;
  /** ★**いまロック中なら解除予定時刻**、そうでなければ `null`（切れた過去のロックは出ない） */
  lockedUntil: string | null;
  /** ★次のログインで本人による変更が必要。manager も状態として閲覧できる */
  mustChangePassword: boolean;
  /**
   * ★**保存されているパスワードが壊れている。正しいパスワードでも入れない**（S5）。
   *   ロック（時間で切れる）と違い、放っておいても直らない。★ハッシュそのものは載らない
   */
  passwordHashBroken: boolean;
  /** ★紐付けられている従業員の ID。未紐付けなら `null`（R0102 A-2） */
  staffId: string | null;
}

/** 紐付けの履歴1件（権限マトリクス §3.6・R0102 A-2 周回2）。★氏名・時給は含まない */
export interface StaffAccountLink {
  id: string;
  loginId: string;
  linkedAt: string;
  unlinkedAt: string | null;
  /** ★誤登録の訂正（窓の取り消し）で無かったことにされた窓か */
  voided: boolean;
}

/** ★平文はこの成功応答で一度だけ届く。保存・再取得するAPIは作らない */
/** 未使用の招待QR（ACCOUNT-INVITE-QR）。★コードは含まない（サーバはハッシュしか持たない） */
export interface InviteStaff {
  id: string;
  staffCode: string;
  displayName: string;
}

export interface AccountInvite {
  targetStaff: InviteStaff | null;
  id: string;
  role: NonNullable<Account["role"]>;
  /** 発行した owner のログインID */
  issuedByLoginId: string;
  /** 日本時間 `YYYY-MM-DD HH:mm` */
  issuedAt: string;
  expiresAt: string;
}

export interface TemporaryPasswordResult {
  temporaryPassword: string;
  userId?: string;
}

/** 時給の履歴1件。★氏名は入らない（サーバが選択していない） */
export interface WageHistory {
  staffId: number;
  baseHourlyWage?: number;
  validFrom: string;
  validTo: string | null;
  reason: string | null;
}

/** 一括改定から外した従業員1名（M5c）。★C-14 の判定からは外れていない */
export interface WageExclusion {
  staffId: number;
  reason: string;
  /** ★理由が古くなったことに気づけるよう、理由と並べて出す */
  excludedAt: string;
}

export interface WagesResult {
  date: string;
  /** その日に有効な時給。★在籍は `hired_on` / `retired_on` で絞られている */
  wages: { staffId: number; baseHourlyWage?: number }[];
  history: WageHistory[];
  /** ★時給の入力上限（0061）。画面の `maxlength` の材料。止める権威は `reviseStaffWage` */
  maxHourlyWage: number;
  minimumWage: MinimumWageCheck;
  /**
   * ★一括改定の適用開始日の既定値。**画面が見ている日ではない。**
   * その最低賃金が有効になった日（解決できなければ `null`）。
   */
  minimumWageFrom: string | null;
  /** ★`minimumWage` から引かれていない。判定と対象は別（M5c AC-16） */
  exclusions: WageExclusion[];
}

export interface BulkWageRaiseResult {
  result: {
    validFrom: string;
    minimumWage: number;
    raisedStaffIds: number[];
    skippedStaffIds: number[];
  };
  /** ★改定後に引き直した C-14。**外した人が残っていれば failed のまま** */
  minimumWage: MinimumWageCheck;
}

/** 勤務実績1行（権限マトリクス §3.3・R0102 A-2）。★氏名・時給は含まない */
export interface WorkRecord {
  staffId: string;
  workDate: string;
  normalMinutes: number;
  nightMinutes: number;
  source: "IMPORT" | "MANUAL" | "PUNCH";
}

export interface WorkRecordsResult {
  records: WorkRecord[];
  /** ★本人が誰にも紐付けられていないときだけ `true`（AC-8。黙って空にしない） */
  unlinked?: boolean;
  message?: string;
}

export const api = {
  getDocuments: (storeId: string, businessDate: string) =>
    request<{ documents: IntakeDocument[] }>(
      `/api/documents?storeId=${encodeURIComponent(storeId)}&businessDate=${encodeURIComponent(businessDate)}`,
    ),
  uploadDocument: (storeId: string, input: { businessDate: string; docType: DocumentType; mimeType: string; imageBase64: string; originalFilename: string; clientRequestId: string }) =>
    request<{ document: { id: string; workflow_status: DocumentWorkflowStatus; duplicate: boolean } }>("/api/documents", {
      method: "POST",
      body: JSON.stringify({ storeId, ...input }),
    }),
  getDocumentImage: (storeId: string, id: string) =>
    request<{ mimeType: string; imageBase64: string }>(
      `/api/documents/${encodeURIComponent(id)}/image?storeId=${encodeURIComponent(storeId)}`,
    ),
  runDocumentOcr: (storeId:string,id:string) => request<{draft:DocumentOcrDraft;version:number}>(`/api/documents/${encodeURIComponent(id)}/ocr`,{method:"POST",body:JSON.stringify({storeId})}),
  skipDocumentOcr: (storeId:string,id:string) => request<{draft:DocumentOcrDraft;version:number}>(`/api/documents/${encodeURIComponent(id)}/ocr/skip`,{method:"POST",body:JSON.stringify({storeId})}),
  saveDocumentDraft: (storeId:string,id:string,version:number,draft:DocumentOcrDraft) => request<{draft:DocumentOcrDraft;version:number}>(`/api/documents/${encodeURIComponent(id)}/draft`,{method:"PUT",body:JSON.stringify({storeId,version,draft})}),
  registerDocument: (storeId:string,id:string,kind:"purchase"|"petty-cash"|"sales",payload:Record<string,unknown>) => request<{registration:{target:string;id:string;duplicate:boolean}}>(`/api/documents/${encodeURIComponent(id)}/register/${kind}`,{method:"POST",body:JSON.stringify({storeId,...payload})}),
  // 日報（docs/05 §3.1・manager 以上）。★数値はサーバが計算した `figures`（docs/02 §2.4）。画面で計算しない
  getDailyReport: (storeId: string, date: string) =>
    request<{
      report: { businessDate: string; comment: string; updatedAt: string } | null;
      figures: ReportFigures;
      /** 報告文の末尾の定型文（未設定は null） */
      footer: string | null;
    }>(`/api/daily-report/${encodeURIComponent(date)}?storeId=${encodeURIComponent(storeId)}`),
  saveDailyReport: (storeId: string, date: string, comment: string) =>
    request<{ report: { businessDate: string; comment: string; updatedAt: string } }>(
      `/api/daily-report/${encodeURIComponent(date)}`,
      { method: "PUT", body: JSON.stringify({ storeId, comment }) },
    ),
  saveDailyReportForecast: (
    storeId: string,
    date: string,
    forecast: {
      purchaseEstimate: number | null;
      salesEstimate: number | null;
      reservationGroups?: number | null;
      reservationGuests?: number | null;
      courseGuests?: number | null;
      shiftStaff?: number | null;
    },
  ) =>
    request<{ forecast: Record<string, number | null> }>(
      `/api/daily-report/${encodeURIComponent(date)}/forecast`,
      { method: "PUT", body: JSON.stringify({ storeId, ...forecast }) },
    ),
  // ③ 予約サイトから翌日の予約を読む（docs/21 §3・manager 以上）。DB には書かない。引くのは date の翌日
  getReservationSummary: (storeId: string, date: string) =>
    request<{ reservationSummary: { date: string; reservationGroups: number; reservationGuests: number; courseGuests: number } }>(
      `/api/daily-report/${encodeURIComponent(date)}/reservation-summary?storeId=${encodeURIComponent(storeId)}`,
    ),
  // 報告文の定型文（docs/05 §3.4・manager 以上）。text が null なら消す
  getLineReportFooter: (storeId: string) =>
    request<{ footer: string | null }>(`/api/settings/line-report-footer?storeId=${encodeURIComponent(storeId)}`),
  saveLineReportFooter: (storeId: string, text: string | null) =>
    request<{ footer: string | null }>("/api/settings/line-report-footer", {
      method: "PUT",
      body: JSON.stringify({ storeId, text }),
    }),

  login: (loginId: string, password: string, persistent: boolean) =>
    request<{ mustChangePassword: boolean; isPersistent: boolean }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ loginId, password, persistent }),
    }),

  logout: () => request<{ ok: true }>("/api/auth/logout", { method: "POST" }),

  me: () => request<Me>("/api/me"),

  /** ★現在のパスワードは必須。省略できる形にしない（B-2） */
  /** ★初回の強制変更では `currentPassword` を送らない（R-08）。あとから変えるときは必須 */
  /** ★`newLoginId` はIDをまだ決めていない初回だけ送る（SELF-LOGIN-ID） */
  changePassword: (currentPassword: string | undefined, newPassword: string, newLoginId?: string) =>
    request<{ ok: true }>("/api/auth/password", {
      method: "POST",
      body: JSON.stringify({ currentPassword, newPassword, newLoginId }),
    }),

  getSales: (storeId: string, date: string) =>
    request<{
      sales: DailySales | null;
      closingStatus: ClosingStatus;
      ceilings: InputCeilings;
      /** 当月の、記録の無い営業日（§2.1）。カレンダー未整備の月は null */
      missingDates: string[] | null;
    }>(
      `/api/sales/${date}?storeId=${encodeURIComponent(storeId)}`,
    ),

  putSales: (storeId: string, date: string, input: Record<string, unknown>) =>
    request<{ sales: DailySales }>(`/api/sales/${date}`, {
      method: "PUT",
      body: JSON.stringify({ storeId, ...input }),
    }),

  getLoss: (storeId: string, date: string) =>
    request<{ records: LossRecord[] }>(
      `/api/loss/${date}?storeId=${encodeURIComponent(storeId)}`,
    ),

  addLoss: (storeId: string, date: string, input: Record<string, unknown>) =>
    request<{ record: LossRecord }>(`/api/loss/${date}`, {
      method: "POST",
      body: JSON.stringify({ storeId, ...input }),
    }),

  deleteLoss: (storeId: string, id: string) =>
    request<{ ok: true }>(`/api/loss/${id}?storeId=${encodeURIComponent(storeId)}`, {
      method: "DELETE",
    }),

  /** ★棚卸は月単位（`YYYY-MM`）。日付ではない */
  getExpenseAccounts: (storeId: string) =>
    request<{ accounts: ExpenseAccount[] }>(
      `/api/expense-accounts?storeId=${encodeURIComponent(storeId)}`,
    ),

  /** ★科目は常に全件返る。未入力の月額は `null`（0 と区別する） */
  getFixedCosts: (storeId: string, month: string) =>
    request<{
      fixedCosts: FixedCost[];
      closingStatus: ClosingStatus;
      /** ★書ける人（owner）にだけ返る。読むだけの人には `undefined` */
      ceilings?: FixedCostCeilings;
    }>(`/api/fixed-costs/${month}?storeId=${encodeURIComponent(storeId)}`),

  getInventory: (storeId: string, month: string) =>
    request<{
      inventories: Inventory[];
      closingStatus: ClosingStatus;
      ceilings: InventoryCeilings;
    }>(`/api/inventory/${month}?storeId=${encodeURIComponent(storeId)}`),

  /** ★日付を渡す。当月はその日付から決まる（`date` に3つ目の意味を持たせない） */
  /** §3.2（manager 以上）。★staff / leader は 403 なので呼ばない */
  getProfitLoss: (storeId: string, date: string) =>
    request<{ profitLoss: ProfitLoss; anomalies: DailyAnomalies }>(
      `/api/profit-loss/${date}?storeId=${encodeURIComponent(storeId)}`,
    ),
  /** 帳票「日次決算書」（F-13・§3.2「帳票出力」：owner のみ）。★取得するたびに EXPORT が監査ログへ残る */
  getDailyStatement: (storeId: string, yearMonth: string) =>
    request<{ statement: DailyStatement }>(
      `/api/reports/daily-statement/${encodeURIComponent(yearMonth)}?storeId=${encodeURIComponent(storeId)}`,
    ),
  getSalesSummary: (storeId: string, date: string) =>
    request<{ summary: SalesSummary }>(
      `/api/sales-summary/${date}?storeId=${encodeURIComponent(storeId)}`,
    ),

  getMonthlyTargets: (storeId: string, month: string) =>
    request<{ targets: MonthlyTarget[]; ceilings?: MonthlyTargetCeilings }>(
      `/api/monthly-targets/${month}?storeId=${encodeURIComponent(storeId)}`,
    ),

  /** ★利益目標・目標FL。manager 以上だけ（`docs/05` §3.4 の別の行）。値の率は 0〜1 の小数 */
  getProfitTargets: (storeId: string, month: string) =>
    request<{ targets: MonthlyTarget[] }>(
      `/api/profit-targets/${month}?storeId=${encodeURIComponent(storeId)}`,
    ),

  putProfitTargets: (storeId: string, month: string, targets: Record<string, unknown>[]) =>
    request<{ targets: MonthlyTarget[] }>(`/api/profit-targets/${month}`, {
      method: "PUT",
      body: JSON.stringify({ storeId, targets }),
    }),

  putMonthlyTargets: (storeId: string, month: string, targets: Record<string, unknown>[]) =>
    request<{ targets: MonthlyTarget[] }>(`/api/monthly-targets/${month}`, {
      method: "PUT",
      body: JSON.stringify({ storeId, targets }),
    }),

  getSalesTargets: (storeId: string, month: string) =>
    request<{ targets: DailySalesTarget[]; ceilings?: SalesTargetCeilings }>(
      `/api/sales-targets/${month}?storeId=${encodeURIComponent(storeId)}`,
    ),

  putSalesTargets: (storeId: string, month: string, targets: Record<string, unknown>[]) =>
    request<{ targets: DailySalesTarget[] }>(`/api/sales-targets/${month}`, {
      method: "PUT",
      body: JSON.stringify({ storeId, targets }),
    }),

  getCalendar: (storeId: string, month: string) =>
    request<{ calendar: CalendarDraft }>(
      `/api/calendar/${month}?storeId=${encodeURIComponent(storeId)}`,
    ),

  /** ★**月まるごと**送る。1日だけの保存はサーバが拒む（欠けた月を作らせない） */
  putCalendar: (storeId: string, month: string, days: Record<string, unknown>[]) =>
    request<{ days: CalendarDay[] }>(`/api/calendar/${month}`, {
      method: "PUT",
      body: JSON.stringify({ storeId, days }),
    }),

  getParameters: (storeId: string, date: string) =>
    request<{
      date: string;
      parameters: ParametersResult;
      minimumWage: MinimumWageCheck;
      /**
       * ★owner だけが書けるキー（原典 §3.4.1 / R-05）。**サーバが持つ一覧をそのまま受ける。**
       *   画面側にコピーを持つと、増えたときに「選べるのに 403」か
       *   「選べないのに書ける」が静かに生まれる。禁止しているのはサーバ側の
       *   `assertCanWriteParameter` で、これは**選択肢を出し分けるためだけ**の情報。
       */
      ownerOnlyKeys: string[];
    }>(
      `/api/parameters?storeId=${encodeURIComponent(storeId)}&date=${encodeURIComponent(date)}`,
    ),

  reviseParameter: (storeId: string, key: string, input: Record<string, unknown>) =>
    request<{
      revision: { key: string; value: string; validFrom: string; closedPreviousAt: string | null };
      minimumWage: MinimumWageCheck | null;
    }>(`/api/parameters/${encodeURIComponent(key)}`, {
      method: "POST",
      body: JSON.stringify({ storeId, ...input }),
    }),

  /**
   * ★どのタブを開いても呼ぶ（AC-1）。**日付を渡さない**——
   *   検査する日はサーバが決める（今日＋未来の改定日）。画面の日付に寄せない。
   */
  getMinimumWageAlert: (storeId: string) =>
    request<{ alert: MinimumWageAlert; truncated: boolean }>(
      `/api/wages/minimum-alert?storeId=${encodeURIComponent(storeId)}`,
    ),

  /** 未退勤・不整合の検出（権限マトリクス §3.6・R0102 A-3）。`(個)` の絞り込みはサーバが行う */
  getAttendanceAnomalies: (storeId: string, month: string) =>
    request<{ anomalies: AttendanceAnomaly[] }>(
      `/api/attendance-anomalies?storeId=${encodeURIComponent(storeId)}&month=${encodeURIComponent(month)}`,
    ),

  /**
   * 打刻を記録する（権限マトリクス §3.6「打刻」= 全ロール W(個)・R0102 A-4）。
   * ★対象の従業員（`staffId`）は渡さない。サーバが自分の紐付けから解決する
   *   （他人の代打が原理的にできない設計。`src/infra/punches.ts` の注記と同じ）。
   */
  recordPunch: (storeId: string, punchType: PunchType) =>
    request<{ punch: PunchEvent }>("/api/punches", {
      method: "POST",
      body: JSON.stringify({ storeId, punchType }),
    }),

  /**
   * 打刻イベント・訂正履歴の閲覧（§3.6：同じ行。`(個)` の絞り込みはサーバが行う）。
   * - `scope: "own"` … ロールに関わらず自分の分だけ（打刻タブ。manager/owner も自分のボタン
   *   状態はここから。R0102 A-5 AC-8）
   * - `staffId` … その1名分（manager/owner の従業員選択ビュー。他ロールはサーバが 403）
   */
  /**
   * 確定シフトの閲覧（§3.7：全ロール `R`・**店舗全体・氏名つき**。R0102 B-5）。
   * ★**ロールで中身が変わらない。** 画面の「自分の分だけ」は表示の絞り込みであって
   *   可視性の境界ではない（サーバへ取りに行かない）。
   */
  /**
   * シフトの変更・公開への気づき（§3.7 の Q-S6 / R0102 B-7）。**全ロール**。
   * ★`month` を取らない——「自分のどこかが変わったか」だけを聞く。
   */
  getShiftNotice: (storeId: string) =>
    request<ShiftNoticeResult>(`/api/shift-notice?storeId=${encodeURIComponent(storeId)}`),

  getShifts: (storeId: string, month: string) =>
    request<ShiftsResult>(
      `/api/shifts?storeId=${encodeURIComponent(storeId)}&month=${encodeURIComponent(month)}`,
    ),

  /** 確定シフトを置く（§3.7：manager 以上・Q-S2） */
  createShift: (storeId: string, input: ShiftInput) =>
    request<{ shift: Shift }>("/api/shifts", {
      method: "POST",
      body: JSON.stringify({ storeId, ...input }),
    }),

  /**
   * 確定シフトの時間帯・休憩を変更する（§3.7：manager 以上）。
   * ★`businessDate` / `staffId` は動かせない（動かしたいなら取り消して置き直す）
   */
  updateShift: (
    storeId: string,
    id: string,
    times: {
      startMinutes: number;
      endMinutes: number;
      breaks: { startMinutes: number; endMinutes: number }[];
    },
  ) =>
    request<{ shift: Shift }>(`/api/shifts/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify({ storeId, ...times }),
    }),

  /**
   * 確定シフトを取り消す（§3.7：「作成・変更・取消」の同じ行）。
   * ★**物理削除ではない。** サーバは `CANCELED` にするだけで行を消さない
   */
  cancelShift: (storeId: string, id: string) =>
    request<{ ok: true }>(`/api/shifts/${encodeURIComponent(id)}/cancel`, {
      method: "POST",
      body: JSON.stringify({ storeId }),
    }),

  /**
   * 確定シフトを公開する（§3.7 の「公開」の行：manager 以上。R0102 B-6）。
   * ★**営業日の範囲に対する操作**（両端を含む・同じ月の中）。
   *   月ごとに固定すると、半月ずつ組む運用で**組みかけの後半まで一緒に公開される。**
   */
  publishShifts: (storeId: string, from: string, to: string) =>
    request<{ published: number }>("/api/shifts/publish", {
      method: "POST",
      body: JSON.stringify({ storeId, from, to }),
    }),

  /**
   * 公開を取り消して下書きへ戻す（§3.7 の同じ行）。★取消（`CANCELED`）とは別物。
   * ★範囲を取るのは公開と同じ理由——**月ごとに戻すと、既に周知した前半まで消える。**
   */
  unpublishShifts: (storeId: string, from: string, to: string) =>
    request<{ unpublished: number }>("/api/shifts/unpublish", {
      method: "POST",
      body: JSON.stringify({ storeId, from, to }),
    }),

  /**
   * 希望シフトの一覧（§3.7：閲覧は全ロール。`staff` / `leader` は自分の分だけ）。
   * ★絞るのはサーバ。**画面で他人の分を隠すのではない。**
   */
  getShiftRequests: (storeId: string, month: string) =>
    request<ShiftRequestsResult>(
      `/api/shift-requests?storeId=${encodeURIComponent(storeId)}&month=${encodeURIComponent(month)}`,
    ),

  /** 希望を出す（§3.7：全ロール・自分の分のみ） */
  createShiftRequest: (
    storeId: string,
    input: { staffId: string; businessDate: string; startMinutes: number; endMinutes: number },
  ) =>
    request<{ request: ShiftRequest }>("/api/shift-requests", {
      method: "POST",
      body: JSON.stringify({ storeId, ...input }),
    }),

  /** 取り下げ（★締切後も通る。出られないと分かる事実は締切より後に判明する） */
  withdrawShiftRequest: (storeId: string, id: string) =>
    request<{ ok: true }>(`/api/shift-requests/${encodeURIComponent(id)}/withdraw`, {
      method: "POST",
      body: JSON.stringify({ storeId }),
    }),

  /** 承認（§3.7：manager 以上）。★確定シフトはサーバ側で作られる */
  approveShiftRequest: (storeId: string, id: string) =>
    request<{ request: ShiftRequest }>(`/api/shift-requests/${encodeURIComponent(id)}/approve`, {
      method: "POST",
      body: JSON.stringify({ storeId }),
    }),

  /** 却下（§3.7：manager 以上・**理由必須**） */
  rejectShiftRequest: (storeId: string, id: string, rejectReason: string) =>
    request<{ request: ShiftRequest }>(`/api/shift-requests/${encodeURIComponent(id)}/reject`, {
      method: "POST",
      body: JSON.stringify({ storeId, rejectReason }),
    }),

  /**
   * 確定シフトの修正申請の一覧（§3.7：閲覧は全ロール。`staff` / `leader` は自分の分だけ）。
   * ★絞るのはサーバ。**画面で他人の分を隠すのではない。**
   */
  getShiftChangeRequests: (storeId: string, month: string) =>
    request<ShiftChangeRequestsResult>(
      `/api/shift-change-requests?storeId=${encodeURIComponent(storeId)}&month=${encodeURIComponent(month)}`,
    ),

  /**
   * 確定シフトの修正を申請する（§3.7：全ロール・自分の分・**要理由**）。
   * ★`shiftPlanId` は対象の確定シフトの id。`staffId` / `businessDate` は渡さない——
   *   サーバが `shiftPlanId` から解決する（本文の値を信用しない設計。B-4 の課題定義）。
   */
  createShiftChangeRequest: (
    storeId: string,
    input: { shiftPlanId: string; startMinutes: number; endMinutes: number; reason: string },
  ) =>
    request<{ request: ShiftRequest }>("/api/shift-change-requests", {
      method: "POST",
      body: JSON.stringify({ storeId, ...input }),
    }),

  /** 取り下げ（希望と同じ形。修正申請には募集の締切が無いので窓の考慮は無い） */
  withdrawShiftChangeRequest: (storeId: string, id: string) =>
    request<{ ok: true }>(`/api/shift-change-requests/${encodeURIComponent(id)}/withdraw`, {
      method: "POST",
      body: JSON.stringify({ storeId }),
    }),

  /** 承認（§3.7：manager 以上）。★確定シフトはサーバ側で更新される（新しい行を作らない） */
  approveShiftChangeRequest: (storeId: string, id: string) =>
    request<{ request: ShiftRequest }>(
      `/api/shift-change-requests/${encodeURIComponent(id)}/approve`,
      { method: "POST", body: JSON.stringify({ storeId }) },
    ),

  /** 却下（§3.7：manager 以上・**理由必須**） */
  rejectShiftChangeRequest: (storeId: string, id: string, rejectReason: string) =>
    request<{ request: ShiftRequest }>(
      `/api/shift-change-requests/${encodeURIComponent(id)}/reject`,
      { method: "POST", body: JSON.stringify({ storeId, rejectReason }) },
    ),

  getPunches: (storeId: string, month: string, opts: { scope?: "own"; staffId?: string } = {}) =>
    request<PunchLogResult>(
      `/api/punches?storeId=${encodeURIComponent(storeId)}&month=${encodeURIComponent(month)}` +
        (opts.scope !== undefined ? `&scope=${opts.scope}` : "") +
        (opts.staffId !== undefined ? `&staffId=${encodeURIComponent(opts.staffId)}` : ""),
    ),

  /**
   * 打刻の訂正（§3.6：自分の分は全ロール・他人の分は manager 以上。理由必須・R0102 A-5）。
   * ★本人/他人の判定はサーバが紐付けから行う。クライアントは主張しない
   */
  correctPunch: (
    storeId: string,
    punchId: string,
    field: "occurred_at" | "punch_type",
    newValue: string,
    reason: string,
  ) =>
    request<{ correction: PunchCorrection }>(
      `/api/punches/${encodeURIComponent(punchId)}/corrections`,
      { method: "PUT", body: JSON.stringify({ storeId, field, newValue, reason }) },
    ),

  /**
   * 勤怠データのCSV出力（§3.6：owner のみ・R0102 A-4）。
   * ★JSON ではないので `request()` を使わない。**URL を組み立てるだけ**——
   *   ダウンロードは画面側がブラウザの通常のナビゲーションに任せる（Cookie は自動で乗る）。
   */
  attendanceExportUrl: (storeId: string, from: string, to: string) =>
    `/api/attendance-export?storeId=${encodeURIComponent(storeId)}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,

  /** 全データのエクスポート（F-18・§3.2：owner のみ）。一覧は JSON、本体は1表ずつの CSV（URL を組み立てるだけ） */
  getExportTables: (storeId: string) =>
    request<{ tables: string[] }>(`/api/data-export?storeId=${encodeURIComponent(storeId)}`),
  dataExportUrl: (storeId: string, table: string) =>
    `/api/data-export/${encodeURIComponent(table)}?storeId=${encodeURIComponent(storeId)}`,

  getStaff: (storeId: string) =>
    request<{ staff: Staff[] }>(`/api/staff?storeId=${encodeURIComponent(storeId)}`),

  addStaff: (storeId: string, input: Record<string, unknown>) =>
    request<{ staff: Staff }>("/api/staff", {
      method: "POST",
      body: JSON.stringify({ storeId, ...input }),
    }),

  /** `retiredOn: null` で在籍へ戻す */
  setStaffRetirement: (storeId: string, id: string, retiredOn: string | null) =>
    request<{ staff: Staff }>(`/api/staff/${encodeURIComponent(id)}/retirement`, {
      method: "PUT",
      body: JSON.stringify({ storeId, retiredOn }),
    }),

  setStaffEmploymentType: (storeId: string, id: string, employmentType: Staff["employmentType"]) =>
    request<{ staff: Staff }>(`/api/staff/${encodeURIComponent(id)}/employment-type`, {
      method: "PUT",
      body: JSON.stringify({ storeId, employmentType }),
    }),

  getAccounts: (storeId: string) =>
    request<{ accounts: Account[] }>(`/api/accounts?storeId=${encodeURIComponent(storeId)}`),

  /** 従業員とアカウントの紐付け／解除（§3.6・R0102 A-2）。★`userId: null` で解除 */
  setStaffLink: (storeId: string, staffId: string, userId: string | null) =>
    request<{ staff: Staff }>(`/api/staff/${encodeURIComponent(staffId)}/link`, {
      method: "PUT",
      body: JSON.stringify({ storeId, userId }),
    }),

  /** 紐付けの履歴（§3.6・R0102 A-2 周回2）。read は manager 以上 */
  getStaffLinks: (storeId: string, staffId: string) =>
    request<{ links: StaffAccountLink[] }>(
      `/api/staff/${encodeURIComponent(staffId)}/links?storeId=${encodeURIComponent(storeId)}`,
    ),

  /** 誤った紐付けの訂正（紐付けの取り消し＝void）。★owner のみ・理由必須・開いている窓は不可 */
  voidStaffLink: (storeId: string, linkId: string, reason: string) =>
    request<{ link: { id: string } }>(`/api/staff-links/${encodeURIComponent(linkId)}/void`, {
      method: "PUT",
      body: JSON.stringify({ storeId, reason }),
    }),

  /**
   * 勤務実績（§3.3・R0102 A-2）。
   * ★`staffId` は manager/owner が1名に絞るときだけ渡す。staff/leader が渡すと、
   *   自分の窓に無ければ 403（一律の権限エラー・T-4）。
   */
  getWorkRecords: (storeId: string, month: string, staffId?: string) =>
    request<WorkRecordsResult>(
      `/api/work-records?storeId=${encodeURIComponent(storeId)}&month=${encodeURIComponent(month)}` +
        (staffId !== undefined ? `&staffId=${encodeURIComponent(staffId)}` : ""),
    ),

  /** ★パスワードを送らない。安全な一時値はサーバーが生成する */
  createAccount: (
    storeId: string,
    input: { loginId: string; displayName: string; role: NonNullable<Account["role"]> },
  ) =>
    request<TemporaryPasswordResult>("/api/accounts", {
      method: "POST",
      body: JSON.stringify({ storeId, ...input }),
    }),

  /**
   * アカウントを止める／戻す（F-21）。★**止めた日は送らない。** サーバが当日を入れる。
   */
  setAccountActive: (storeId: string, id: string, active: boolean) =>
    request<{ accounts: Account[] }>(`/api/accounts/${encodeURIComponent(id)}/active`, {
      method: "PUT",
      body: JSON.stringify({ storeId, active }),
    }),

  /**
   * ログインロックアウトを解除する（S1）。★**owner のみ**（`user_admin`）。
   * ★向きを送らない。この経路は解除しかしない。
   */
  unlockAccount: (storeId: string, id: string) =>
    request<{ accounts: Account[] }>(`/api/accounts/${encodeURIComponent(id)}/unlock`, {
      method: "PUT",
      body: JSON.stringify({ storeId }),
    }),

  /** この店舗でのロールを置き換える（USER-ROLE-CHANGE）。★owner のみ（`user_admin`） */
  changeAccountRole: (storeId: string, id: string, role: NonNullable<Account["role"]>) =>
    request<{ accounts: Account[] }>(`/api/accounts/${encodeURIComponent(id)}/role`, {
      method: "PUT",
      body: JSON.stringify({ storeId, role }),
    }),

  /** 招待QRを発行する。★**応答の `code` が平文を得る唯一の場所**（owner のみ） */
  issueAccountInvite: (storeId: string, role: NonNullable<Account["role"]>, staffId: string | null) =>
    request<{ id: string; code: string; role: NonNullable<Account["role"]>; expiresAt: string; targetStaff: InviteStaff | null }>("/api/account-invites", {
      method: "POST",
      body: JSON.stringify({ storeId, role, staffId }),
    }),

  getAccountInvites: (storeId: string) =>
    request<{ invites: AccountInvite[]; candidates: InviteStaff[] }>(`/api/account-invites?storeId=${encodeURIComponent(storeId)}`),

  revokeAccountInvite: (storeId: string, id: string) =>
    request<{ invites: AccountInvite[] }>(`/api/account-invites/${encodeURIComponent(id)}/revoke`, {
      method: "POST",
      body: JSON.stringify({ storeId }),
    }),

  /** 招待を使ってアカウントを作る。★**未ログイン**で叩く。コードは本文で送る（URL に載せない） */
  redeemAccountInvite: (input: {
    inviteCode: string;
    loginId: string;
    displayName: string;
    password: string;
  }) =>
    request<{ ok: true }>("/api/auth/redeem-invite", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  /** ★向きも平文も送らない。この経路は他人の一時パスワード再発行だけ */
  resetAccountPassword: (storeId: string, id: string) =>
    request<TemporaryPasswordResult>(
      `/api/accounts/${encodeURIComponent(id)}/password-reset`,
      { method: "POST", body: JSON.stringify({ storeId }) },
    ),

  getWages: (storeId: string, date: string) =>
    request<WagesResult>(
      `/api/wages?storeId=${encodeURIComponent(storeId)}&date=${encodeURIComponent(date)}`,
    ),

  /**
   * ★最低賃金への一括改定（M5c）。**誰を上げるかも、いくらにするかも送らない。**
   *   対象も金額もサーバが決める（絶対ルール2）。送るのは日付1つだけ。
   *   その日が判定日であり、適用開始日でもある——**割れる余地を残さない。**
   */
  bulkRaiseWagesToMinimum: (storeId: string, date: string) =>
    request<BulkWageRaiseResult>("/api/wages/bulk-minimum", {
      method: "POST",
      body: JSON.stringify({ storeId, date }),
    }),

  /** `reason: null` で外すのを取り消す */
  setWageExclusion: (storeId: string, staffId: number, reason: string | null) =>
    request<{ exclusion: WageExclusion | null }>(
      `/api/wages/exclusions/${encodeURIComponent(String(staffId))}`,
      { method: "PUT", body: JSON.stringify({ storeId, reason }) },
    ),

  reviseWage: (storeId: string, input: Record<string, unknown>) =>
    request<{
      revision: { staffId: number; validFrom: string; closedPreviousAt: string | null };
      minimumWage: MinimumWageCheck;
    }>("/api/wages", { method: "POST", body: JSON.stringify({ storeId, ...input }) }),

  /** ★`source` は送らない。`DERIVED` はサーバが作らせない（計算定義書 §6.1） */
  addExpenseAccount: (storeId: string, input: Record<string, unknown>) =>
    request<{ account: ExpenseAccount }>("/api/expense-accounts", {
      method: "POST",
      body: JSON.stringify({ storeId, ...input }),
    }),

  putFixedCost: (storeId: string, month: string, input: Record<string, unknown>) =>
    request<{ fixedCost: FixedCost }>(`/api/fixed-costs/${month}`, {
      method: "PUT",
      body: JSON.stringify({ storeId, ...input }),
    }),

  putInventory: (storeId: string, month: string, input: Record<string, unknown>) =>
    request<{ inventory: Inventory }>(`/api/inventory/${month}`, {
      method: "PUT",
      body: JSON.stringify({ storeId, ...input }),
    }),

  getVendors: (storeId: string) =>
    request<{ vendors: Vendor[] }>(`/api/vendors?storeId=${encodeURIComponent(storeId)}`),

  addVendor: (storeId: string, input: Record<string, unknown>) =>
    request<{ vendor: Vendor }>("/api/vendors", {
      method: "POST",
      body: JSON.stringify({ storeId, ...input }),
    }),

  getPurchases: (storeId: string, date: string) =>
    request<{ purchases: Purchase[]; closingStatus: ClosingStatus; ceilings: PurchaseCeilings }>(
      `/api/purchases/${date}?storeId=${encodeURIComponent(storeId)}`,
    ),

  addPurchase: (storeId: string, date: string, input: Record<string, unknown>) =>
    request<{ purchase: Purchase }>(`/api/purchases/${date}`, {
      method: "POST",
      body: JSON.stringify({ storeId, ...input }),
    }),

  deletePurchase: (storeId: string, id: string) =>
    request<{ ok: true }>(`/api/purchases/${id}?storeId=${encodeURIComponent(storeId)}`, {
      method: "DELETE",
    }),

  getPettyCash: (storeId: string, date: string) =>
    request<{
      entries: PettyCashEntry[];
      accounts: PettyCashAccount[];
      balance: CashBalance;
      cashCount: CashCount | null;
      closingStatus: ClosingStatus;
      ceilings: PettyCashCeilings;
    }>(`/api/petty-cash/${date}?storeId=${encodeURIComponent(storeId)}`),

  addPettyCash: (storeId: string, date: string, input: Record<string, unknown>) =>
    request<{ entry: PettyCashEntry }>(`/api/petty-cash/${date}`, {
      method: "POST",
      body: JSON.stringify({ storeId, ...input }),
    }),

  addPettyCashAccount: (storeId: string, input: Record<string, unknown>) =>
    request<{ account: PettyCashAccount }>("/api/petty-cash-accounts", {
      method: "POST",
      body: JSON.stringify({ storeId, ...input }),
    }),

  /**
   * その月の自動検算を実行して返す（保存はしない）。
   * ★14件すべてが返る。**未実装の検査も `skipped` として含まれる。**
   */
  /**
   * 監査ログを新しい順に（F-22a）。★**変わった列だけ**が返る。
   */
  getAudit: (storeId: string, params: Record<string, string> = {}) =>
    request<AuditPage>(
      `/api/audit?${new URLSearchParams({ storeId, ...params }).toString()}`,
    ),

  /** システム状態の自己診断（F-19）。read は manager 以上 */
  getSelfDiagnosis: (storeId: string) =>
    request<SelfDiagnosisResult>(`/api/self-diagnosis?storeId=${encodeURIComponent(storeId)}`),

  getClosingChecks: (storeId: string, month: string) =>
    request<ClosingChecks>(
      `/api/closings/${month}/checks?storeId=${encodeURIComponent(storeId)}`,
    ),

  /**
   * 月次締めを申請する。
   * ★通らなかったときは 409 で `blocking` が返る（`ApiError` になる）。
   *   **止まった理由は画面で名指しにする。**「できません」だけにしない。
   */
  submitClosing: (storeId: string, month: string) =>
    request<{ month: string; closingStatus: ClosingStatus; checks: IntegrityCheck[] }>(
      `/api/closings/${month}/submit`,
      { method: "POST", body: JSON.stringify({ storeId }) },
    ),

  /** M10：owner だけ（§3.5 / §4）。`reason` は差戻し・解除で必須 */
  decideClosing: (storeId: string, month: string, action: "confirm" | "reject" | "reopen", reason?: string) =>
    request<{ month: string; closingStatus: ClosingStatus }>(`/api/closings/${month}/${action}`, {
      method: "POST",
      body: JSON.stringify({ storeId, reason }),
    }),

  recordCashCount: (storeId: string, date: string, input: Record<string, unknown>) =>
    request<{ cashCount: CashCount }>(`/api/cash-count/${date}`, {
      method: "PUT",
      body: JSON.stringify({ storeId, ...input }),
    }),
};
